'use client'

import { ReactNode, useCallback } from 'react'
import type { MetaValueSchema } from '@/config/metadata-schemas.config'
import {
  createEmptyObjectRow,
  type BlobObjectRow,
} from '@/utils/metadata-form.utils'
import styles from './MetadataManagementTool.module.css'

type Props = {
  id: string
  /** Singular label for one row, e.g. "Slide" or "Section" */
  itemLabel: string
  properties: Readonly<Record<string, MetaValueSchema>>
  value: BlobObjectRow[]
  onChange: (next: BlobObjectRow[]) => void
}

const humanize = (key: string): string => (
  key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (char) => char.toUpperCase())
)

/**
 * Generic list editor for an array of objects described by a property map
 * (string / number / boolean / enum scalars). Used by MetadataBlobEditor for
 * slider items, pricing sections, exhibition info sections, etc.
 */
const MetadataObjectListEditor = ({
  id,
  itemLabel,
  properties,
  value,
  onChange,
}: Props): ReactNode => {
  const propertyEntries = Object.entries(properties)

  const updateRow = useCallback((index: number, key: string, nextValue: string | boolean | number) => {
    onChange(value.map((row, i) => (i === index ? { ...row, [key]: nextValue } : row)))
  }, [onChange, value])

  const removeRow = useCallback((index: number) => {
    onChange(value.filter((_, i) => i !== index))
  }, [onChange, value])

  const addRow = useCallback(() => {
    onChange([...value, createEmptyObjectRow(properties)])
  }, [onChange, properties, value])

  return (
    <div className={styles.BlobList} data-testid={`${id}-object-list`}>
      {value.length === 0
        ? (
          <p className={styles.BlobEmpty}>No {itemLabel.toLowerCase()}s yet</p>
        )
        : (
          <ul className={styles.BlobListItems}>
            {value.map((row, index) => (
              <li
                key={`${id}-row-${index}`}
                className={styles.BlobRow}
                data-testid={`${id}-row-${index}`}
              >
                <div className={styles.BlobRowHeader}>
                  <span className={styles.BlobRowTitle}>{itemLabel} {index + 1}</span>
                  <button
                    type="button"
                    className={styles.BlobRemoveButton}
                    onClick={() => removeRow(index)}
                    aria-label={`Remove ${itemLabel.toLowerCase()} ${index + 1}`}
                  >
                    Remove
                  </button>
                </div>
                {propertyEntries.map(([key, prop]) => {
                  const inputId = `${id}-${key}-${index}`
                  const label = prop.description || humanize(key)
                  const fieldValue = row[key]

                  if (prop.type === 'boolean') {
                    return (
                      <label key={key} className={styles.BlobCheckboxLabel} htmlFor={inputId}>
                        <input
                          id={inputId}
                          type="checkbox"
                          checked={Boolean(fieldValue)}
                          onChange={(event) => updateRow(index, key, event.target.checked)}
                        />
                        {label}
                      </label>
                    )
                  }

                  if (prop.type === 'string' && prop.enum) {
                    return (
                      <div key={key} className={styles.BlobField}>
                        <label className={styles.BlobFieldLabel} htmlFor={inputId}>{label}</label>
                        <select
                          id={inputId}
                          value={typeof fieldValue === 'string' ? fieldValue : ''}
                          onChange={(event) => updateRow(index, key, event.target.value)}
                        >
                          <option value="">—</option>
                          {prop.enum.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </div>
                    )
                  }

                  if (prop.type === 'number') {
                    return (
                      <div key={key} className={styles.BlobField}>
                        <label className={styles.BlobFieldLabel} htmlFor={inputId}>{label}</label>
                        <input
                          id={inputId}
                          type="number"
                          value={fieldValue === undefined || fieldValue === '' ? '' : String(fieldValue)}
                          onChange={(event) => updateRow(index, key, event.target.value)}
                        />
                      </div>
                    )
                  }

                  // Longer copy fields get a textarea
                  const useTextarea = key === 'description' || key === 'content' || key === 'address'
                  return (
                    <div key={key} className={styles.BlobField}>
                      <label className={styles.BlobFieldLabel} htmlFor={inputId}>{label}</label>
                      {useTextarea
                        ? (
                          <textarea
                            id={inputId}
                            value={typeof fieldValue === 'string' ? fieldValue : ''}
                            onChange={(event) => updateRow(index, key, event.target.value)}
                            rows={2}
                          />
                        )
                        : (
                          <input
                            id={inputId}
                            type="text"
                            value={typeof fieldValue === 'string' ? fieldValue : ''}
                            onChange={(event) => updateRow(index, key, event.target.value)}
                          />
                        )}
                    </div>
                  )
                })}
              </li>
            ))}
          </ul>
        )}
      <button type="button" className={styles.BlobAddButton} onClick={addRow}>
        {`Add ${itemLabel.toLowerCase()}`}
      </button>
    </div>
  )
}

export default MetadataObjectListEditor
